import { PaymentInfo, PaymentPlan } from "@vixi/types";
import { useTranslation } from "@vixi/localization";

/**
 * Extracts the maximum course limit from the current user's plan in PaymentInfo.
 * 
 * The limit is extracted from the plan features text. The function looks for
 * patterns like "publish up to X" or "up to X courses" in the feature categories.
 * 
 * @param paymentInfo - The PaymentInfo object containing plans and features
 * @returns The maximum course limit, or undefined if not found
 * 
 * @example
 * // If the current plan has a feature with text "publish up to 5 of them"
 * const limit = extractMaxCourseLimit(paymentInfo); // Returns 5
 */
export function extractMaxCourseLimit(paymentInfo: PaymentInfo | undefined): number | undefined {
  if (!paymentInfo?.plans) {
    return undefined;
  }

  // Find the current plan (the one with inPlan: true)
  const currentPlan = paymentInfo.plans.find((plan: PaymentPlan) => plan.inPlan === true);

  if (!currentPlan?.features) {
    return undefined;
  }

  // Look for features related to courses
  // The feature title might be "Unlimited AI courses" or similar
  const courseFeature = currentPlan.features.find((feature) => {
    const titleEn = feature.title?.en?.toLowerCase() || "";
    const titleAr = feature.title?.ar?.toLowerCase() || "";
    return (
      titleEn.includes("course") ||
      titleAr.includes("كورس") ||
      titleEn.includes("unlimited") ||
      titleAr.includes("غير محدود")
    );
  });

  if (!courseFeature?.categories) {
    return undefined;
  }

  // Extract number from feature categories text
  // Look for patterns like "publish up to 5", "up to 5 courses", etc.
  for (const category of courseFeature.categories) {
    const textEn = category.text?.en || "";
    const textAr = category.text?.ar || "";

    // Pattern: "publish up to X" or "up to X courses" or "X courses"
    const patterns = [
      /(?:publish|host|create).*?up\s+to\s+(\d+)/i,
      /up\s+to\s+(\d+).*?(?:course|كورس)/i,
      /(\d+).*?(?:course|كورس)/i,
      /(?:كورس|course).*?(\d+)/i,
    ];

    for (const pattern of patterns) {
      const matchEn = textEn.match(pattern);
      const matchAr = textAr.match(pattern);

      if (matchEn?.[1]) {
        return parseInt(matchEn[1], 10);
      }

      if (matchAr?.[1]) {
        return parseInt(matchAr[1], 10);
      }
    }
  }

  return undefined;
}

/**
 * Gets the maximum course limit with a fallback value.
 * 
 * @param paymentInfo - The PaymentInfo object
 * @param fallback - Default value if limit cannot be extracted (default: 3)
 * @returns The maximum course limit or fallback value
 */
export function getMaxCourseLimit(
  paymentInfo: PaymentInfo | undefined,
  fallback: number = 3,
): number {
  return extractMaxCourseLimit(paymentInfo) ?? fallback;
}

